import { DialogComponent } from "./dialog.js";
import { fetchConfig, debounce, collectCartSections } from "./utilities-v2.js";
import {
  CartAddEvent,
  CartUpdateEvent,
  ThemeEvents,
  SellingPlanUpdateEvent,
} from "./events.js";
import {
  syncGiftQuantitiesQueued,
  getCart,
  primeCartCache,
} from "./pyp-gift-manager.js";
// Pick-N GWP rules (ACQ-244). Shared, store-agnostic logic; the Alpine
// custom-box store imports the same module. The drawer keeps its OWN state
// (see #giftSelections) and only borrows these pure rules.
import {
  getGwpTarget,
  isPickComplete,
  sumSelectedQuantity,
  addUnit,
  removeUnit,
  quantityFor,
} from "./gwp-pick-rules.js";
// Pick-N cart-line SHAPE (ACQ-247). Pure + stateless, so a relative import is
// fine (no shared instance to preserve, unlike the queue below).
import {
  buildGiftLineItems,
  groupGiftLinesToSelections,
} from "./gwp-cart-write.js";
// Bare `@theme/*` specifier (importmap) so the drawer's commit serializes on the
// ONE shared queue with the cart drawer / reconciler (ACQ-247 D1).
import { cartOperationQueue } from "@theme/cart-operation-queue";
// Pure line resolver: the parent key handed to this drawer can be dead by the
// time we write (another mutation, e.g. the BAWKDAY free-gift reconcile,
// re-keys the box line), so every parent lookup goes through a fresh cart.
import {
  findParentLine,
  isStaleLineError,
  isCartResponse,
} from "./cart-line-resolver.js";

/**
 * Finds the parent box's CURRENT line in a cart. Gift parents are always
 * subscription lines, so when the exact plan id is unknown any subscribed
 * line for the variant qualifies.
 * @param {any} cart
 * @param {{ uuid?: string | null, key?: string | null, variantId?: string | null, sellingPlanId?: string | null }} parent
 * @returns {any | null}
 */
function resolveParentLine(cart, parent) {
  return findParentLine(cart, {
    lineItemId: parent.uuid || null,
    key: parent.key || null,
    variantId: parent.variantId || null,
    sellingPlanId: parent.sellingPlanId || undefined,
    requireSubscription: true,
  });
}

/**
 * @typedef {Object} ParentItemProperties
 * @property {string} [_line_item_id] - Line item ID
 * @property {string} [_parent_product] - Whether this is a parent product
 */

/**
 * @extends {CustomEvent}
 */
export class GiftSelectedEvent extends CustomEvent {
  /**
   * @param {Object} data
   * @param {string} data.variantId
   * @param {string} data.productTitle
   * @param {string} data.parentItemId
   */
  constructor(data) {
    super(GiftSelectedEvent.eventName, { bubbles: true, detail: data });
  }

  static eventName = "gift:selected";
}

/**
 * @extends {CustomEvent}
 */
export class GiftDrawerOpenEvent extends CustomEvent {
  /**
   * @param {Object} data
   * @param {'preview' | 'selection'} data.mode
   * @param {string} [data.parentItemId]
   * @param {string} [data.parentSellingPlanId]
   */
  constructor(data) {
    super(GiftDrawerOpenEvent.eventName, { bubbles: true, detail: data });
  }

  static eventName = "gift-drawer:open";
}

/**
 * @typedef {object} GiftSelectionDrawerRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} previewHeader
 * @property {HTMLElement} selectionHeader
 * @property {HTMLElement} selectionTitle
 * @property {HTMLElement} selectionItemsHeading
 * @property {HTMLElement} previewItems
 * @property {HTMLElement} selectionItems
 * @property {HTMLElement} [pickItems]
 * @property {HTMLElement} [pickCounter]
 * @property {HTMLElement} [pickCounterText]
 * @property {HTMLElement} [pickCounterDots]
 * @property {HTMLElement} footerText
 * @property {HTMLButtonElement} actionButton
 * @property {HTMLElement} actionButtonText
 * @property {HTMLButtonElement} backButton
 */

/**
 * Gift selection drawer component.
 * Supports preview mode (read-only) and selection mode (interactive).
 * @extends {DialogComponent}
 */
class GiftSelectionDrawerComponent extends DialogComponent {
  /** @type {'preview' | 'selection'} */
  #mode = "preview";

  /** @type {string | null} */
  #selectedGiftId = null;

  /**
   * Pick-N GWP selections (ACQ-244), used only when the pick-2 promo is on
   * (getGwpTarget() >= 2). The drawer owns this state privately; it is NOT the
   * Alpine customBox store. Shape: [{ variantId, quantity, productTitle }].
   * Single-gift mode (target 1) keeps using #selectedGiftId untouched.
   * @type {Array<{ variantId: string, quantity: number, productTitle: string }>}
   */
  #giftSelections = [];

  /**
   * Monotonic token for the async pre-seed (switch). Bumped each time a seed
   * starts so a stale fetch result can detect it was superseded and bail.
   * @type {number}
   */
  #seedRequestId = 0;

  /** @type {string | null} */
  #parentItemId = null;

  /** @type {string | null} */
  #parentItemKey = null;

  /** @type {string | null} */
  #parentSellingPlanId = null;

  /** @type {boolean} */
  #loading = false;

  /** @type {boolean} */
  #hasTrackedSelectionView = false;

  /** @type {boolean} */
  #isSwitching = false;

  /** @type {string | null} */
  #existingGiftLineId = null;

  /** @type {string | null} */
  #originalGiftVariantId = null;

  /** @type {number} */
  #parentQuantity = 1;

  /** @type {boolean} */
  #openedFromLearnMore = false;

  /** @type {boolean} */
  #skipAddonsRedirect = false;

  #abortController = new AbortController();

  /**
   * Guards against concurrent async `#processCartForGift` runs.
   * Rapid cart-add bursts can otherwise interleave and clobber shared state.
   * @type {Promise<void> | null}
   */
  #giftProcessingPromise = null;

  /** @type {{ cart: any, variantId: string, isReAdd: boolean } | null} */
  #pendingGiftProcessingArgs = null;

  #debouncedHandleCartAdd = debounce(
    (event) => this.#handleCartAdd(event),
    150,
  );

  /**
   * Primes shared cart cache with latest snapshot.
   * @param {any} cart
   */
  #primeCart(cart) {
    if (cart && Array.isArray(cart.items)) {
      primeCartCache(cart);
    }
  }

  /**
   * Attempts to read a cart object from an event payload.
   * @param {CustomEvent} event
   * @returns {any | null}
   */
  #extractCartFromEvent(event) {
    const resourceCart = event.detail?.resource;
    const resourceWithItems = resourceCart
      ? /** @type {any} */ (resourceCart)
      : null;
    if (resourceWithItems && Array.isArray(resourceWithItems.items)) {
      return resourceWithItems;
    }

    const dataCart = event.detail?.data?.cart;
    const dataWithItems = dataCart ? /** @type {any} */ (dataCart) : null;
    if (dataWithItems && Array.isArray(dataWithItems.items)) {
      return dataWithItems;
    }

    return null;
  }

  /**
   * Cached cart fetch helper.
   * @param {boolean} [force]
   */
  #getCart(force = false) {
    return /** @type {Promise<any>} */ (getCart({ force }));
  }

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    document.addEventListener(
      CartAddEvent.eventName,
      this.#debouncedHandleCartAdd,
      { signal },
    );
    document.addEventListener(
      SellingPlanUpdateEvent.eventName,
      this.#handleSellingPlanUpdate,
      { signal },
    );
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal,
    });
    this.addEventListener("dialog:close", this.#handleDialogClose, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** @param {CartAddEvent} event */
  #handleCartAdd = (event) => {
    if (event.detail?.data?.didError) return;
    if (event.detail?.data?.source !== "product-form-component") return;

    const sellingPlanId = event.detail?.data?.sellingPlanId;
    if (!sellingPlanId) return;

    // Products excluded from the free gift (GWP Eligible metafield false) never
    // open the gift flow; added-to-cart-drawer opens the cart drawer instead.
    if (event.detail?.data?.gwpExcluded === true) return;

    // Skip for product card adds - those go through the added-to-cart drawer first
    const sourceContext = event.detail?.data?.sourceContext;
    if (sourceContext === "product-card") return;

    const variantId = event.detail?.data?.variantId || event.detail?.sourceId;
    if (!variantId) return;

    const addedQuantity = event.detail?.data?.itemCount || 1;
    const cartData = /** @type {any} */ (event.detail?.data)?.cart;

    if (cartData?.items) {
      this.#primeCart(cartData);
      const subscriptionItem = cartData.items.find(
        /** @param {any} item */
        (item) =>
          item.variant_id?.toString() === variantId.toString() &&
          item.selling_plan_allocation &&
          !item.properties?._is_pyp_gift,
      );
      const isReAdd =
        subscriptionItem && subscriptionItem.quantity > addedQuantity;
      this.#enqueueGiftProcessing(cartData, variantId, isReAdd);
    } else {
      this.#fetchCartAndProcess(variantId, true);
    }
  };

  /**
   * Coalesces concurrent cart-add bursts into a single async processing run.
   * If a run is already in-flight, we keep only the latest args and rerun once.
   * @param {any} cart
   * @param {string} variantId
   * @param {boolean} [isReAdd]
   */
  #enqueueGiftProcessing(cart, variantId, isReAdd = false) {
    this.#pendingGiftProcessingArgs = { cart, variantId, isReAdd };
    void this.#drainGiftProcessingQueue();
  }

  async #drainGiftProcessingQueue() {
    if (this.#giftProcessingPromise) return;

    this.#giftProcessingPromise = (async () => {
      while (this.#pendingGiftProcessingArgs) {
        const args = this.#pendingGiftProcessingArgs;
        this.#pendingGiftProcessingArgs = null;
        await this.#processCartForGift(args.cart, args.variantId, args.isReAdd);
      }
    })();

    try {
      await this.#giftProcessingPromise;
    } finally {
      this.#giftProcessingPromise = null;
    }
  }

  /**
   * @param {any} cart
   * @param {string} variantId
   * @param {boolean} [isReAdd]
   */
  async #processCartForGift(cart, variantId, isReAdd = false) {
    this.#primeCart(cart);

    const subscriptionItems = cart.items?.filter(
      /** @param {any} item */
      (item) =>
        item.variant_id?.toString() === variantId.toString() &&
        item.selling_plan_allocation &&
        !item.properties?._is_pyp_gift,
    );

    if (!subscriptionItems || subscriptionItems.length === 0) return;

    const addedItem = subscriptionItems[subscriptionItems.length - 1];

    let existingGiftItem = null;
    for (const subItem of subscriptionItems) {
      const gift = cart.items?.find(
        /** @param {any} item */
        (item) =>
          item.properties?._is_pyp_gift === "true" &&
          item.properties?._parent_item_id ===
            subItem.properties?._line_item_id,
      );
      if (gift) {
        existingGiftItem = gift;
        break;
      }
    }

    const parentUuid = addedItem.properties?._line_item_id;

    const thisItemGift = cart.items?.find(
      /** @param {any} item */
      (item) =>
        item.properties?._is_pyp_gift === "true" &&
        item.properties?._parent_item_id === parentUuid,
    );

    this.#parentItemId = addedItem.id?.toString() || null;
    this.#parentItemKey = addedItem.key || null;
    this.#parentSellingPlanId =
      addedItem.selling_plan_allocation?.selling_plan?.id?.toString() || null;
    this.#parentQuantity = addedItem.quantity || 1;

    // Pick-N: the pick UI + #commitGiftSelections own all gift handling, so skip
    // the single-gift first-match switch logic below entirely (it would mistake
    // one of two slotted siblings for "the" gift). We still set the parent
    // context above and open the pick selector (ACQ-247 T6/D4).
    if (this.#isPickMode()) {
      this.#isSwitching = false;
      this.#existingGiftLineId = null;
      this.#selectedGiftId = null;
      this.#originalGiftVariantId = null;
      this.showSelectionMode();
      return;
    }

    // Re-add with existing gift: sync quantity immediately, then open switch drawer
    if (thisItemGift && isReAdd) {
      if (thisItemGift.quantity !== this.#parentQuantity) {
        await this.#updateCartItem(
          thisItemGift.key || thisItemGift.id?.toString(),
          this.#parentQuantity,
        );
        document.dispatchEvent(
          new CartUpdateEvent({}, "gift-selection-drawer", {
            source: "gift-selection-drawer",
          }),
        );
      }

      this.#isSwitching = true;
      this.#existingGiftLineId =
        thisItemGift.key || thisItemGift.id?.toString();
      this.#selectedGiftId = thisItemGift.variant_id?.toString() || null;
      this.#originalGiftVariantId = this.#selectedGiftId;
      this.showSelectionMode();
      return;
    }

    if (thisItemGift) return;

    if (existingGiftItem) {
      if (existingGiftItem.quantity !== this.#parentQuantity) {
        await this.#updateCartItem(
          existingGiftItem.key || existingGiftItem.id?.toString(),
          this.#parentQuantity,
        );
        document.dispatchEvent(
          new CartUpdateEvent({}, "gift-selection-drawer", {
            source: "gift-selection-drawer",
          }),
        );
      }

      this.#isSwitching = true;
      this.#existingGiftLineId =
        existingGiftItem.key || existingGiftItem.id?.toString();
      this.#selectedGiftId = existingGiftItem.variant_id?.toString() || null;
      this.#originalGiftVariantId = this.#selectedGiftId;
    } else {
      this.#isSwitching = false;
      this.#existingGiftLineId = null;
      this.#selectedGiftId = null;
      this.#originalGiftVariantId = null;
    }

    this.showSelectionMode();
  }

  /** @param {string} variantId */
  async #fetchCartAndProcess(variantId, force = false) {
    try {
      const cart = await this.#getCart(force);
      this.#primeCart(cart);
      this.#enqueueGiftProcessing(cart, variantId);
    } catch (error) {
      console.error("Error checking cart for gift eligibility:", error);
    }
  }

  /** @param {SellingPlanUpdateEvent} event */
  #handleSellingPlanUpdate = (event) => {
    if (event.detail?.sellingPlanId) {
      this.#parentSellingPlanId = event.detail.sellingPlanId;
    }
  };

  /** @param {CustomEvent} event */
  #handleCartUpdate = async (event) => {
    const source = event.detail?.data?.source;
    if (
      source === "gift-selection-drawer" ||
      source === "pyp-gift-sync" ||
      source === "product-form-component"
    )
      return;
    if (source !== "cart-items-component") return;

    try {
      const cartFromEvent = this.#extractCartFromEvent(event);
      if (cartFromEvent) {
        this.#primeCart(cartFromEvent);
      }

      // Reactive cart:update listener (can fire mid-commit): route the reconcile
      // through the queue with a fresh read inside (ACQ-247 D2) instead of
      // mutating off-queue against this transient event snapshot.
      await syncGiftQuantitiesQueued({ dispatchEvent: true });
    } catch (error) {
      console.error("Error syncing gift quantities:", error);
    }
  };

  showDialog() {
    // Check if opened from learn more drawer
    this.#openedFromLearnMore = this.dataset.openedFromLearnMore === "true";
    this.showPreviewMode();
  }

  showPreviewMode() {
    this.#mode = "preview";
    this.#selectedGiftId = null;
    this.#updateUIForMode();
    this.#updateBackButtonVisibility();
    super.showDialog();
    this.dispatchEvent(new GiftDrawerOpenEvent({ mode: "preview" }));
  }

  /** @type {string | null} */
  #parentUuid = null;

  /**
   * @param {Object} [options]
   * @param {string} [options.parentItemId]
   * @param {string} [options.parentItemKey]
   * @param {string} [options.parentUuid]
   * @param {string} [options.parentSellingPlanId]
   * @param {string} [options.existingGiftLineId]
   * @param {string} [options.existingGiftVariantId]
   * @param {number} [options.parentQuantity]
   * @param {boolean} [options.skipAddonsRedirect]
   */
  showSelectionMode(options = {}) {
    this.#mode = "selection";

    if (options.parentItemId) this.#parentItemId = options.parentItemId;
    if (options.parentItemKey) this.#parentItemKey = options.parentItemKey;
    if (options.parentUuid) this.#parentUuid = options.parentUuid; // ← Store UUID
    if (options.parentSellingPlanId)
      this.#parentSellingPlanId = options.parentSellingPlanId;
    if (options.existingGiftLineId) {
      this.#existingGiftLineId = options.existingGiftLineId;
      this.#isSwitching = true;
    }
    if (options.existingGiftVariantId) {
      this.#selectedGiftId = options.existingGiftVariantId;
    } else if (!this.#isSwitching) {
      this.#selectedGiftId = null;
    }
    if (options.parentQuantity) this.#parentQuantity = options.parentQuantity;
    this.#skipAddonsRedirect = options.skipAddonsRedirect ?? false;

    // A pick-N switch starts from a clean slate; we then pre-seed it (below,
    // after the dialog opens) with the box's current gifts so the pick UI
    // shows them already selected instead of forcing a re-pick from zero.
    const isPickSwitch = this.#isSwitching && this.#isPickMode();
    if (isPickSwitch) this.#giftSelections = [];

    this.#updateUIForMode();
    super.showDialog();

    if (isPickSwitch) void this.#seedPickSelectionsFromCart();

    requestAnimationFrame(() => {
      void this.#trackGiftSelectionViewed();
    });

    this.dispatchEvent(
      new GiftDrawerOpenEvent({
        mode: "selection",
        parentItemId: this.#parentItemId || undefined,
        parentSellingPlanId: this.#parentSellingPlanId || undefined,
      }),
    );
  }

  #updateUIForMode() {
    const {
      previewHeader,
      selectionHeader,
      previewItems,
      selectionItems,
      footerText,
      actionButton,
      actionButtonText,
      selectionTitle,
      selectionItemsHeading,
    } = this.refs;

    const isPreview = this.#mode === "preview";

    if (previewHeader instanceof HTMLElement)
      previewHeader.classList.toggle("hidden", !isPreview);
    if (selectionHeader instanceof HTMLElement)
      selectionHeader.classList.toggle("hidden", isPreview);
    if (previewItems instanceof HTMLElement)
      previewItems.classList.toggle("hidden", !isPreview);
    if (selectionItems instanceof HTMLElement)
      selectionItems.classList.toggle("hidden", isPreview);

    if (!isPreview) {
      if (selectionTitle instanceof HTMLElement) {
        selectionTitle.textContent = this.#isSwitching
          ? this.dataset.switchTitle || "Switch Your Free Gift"
          : this.dataset.selectionTitle || "Select Your Free Gift";
      }
      if (selectionItemsHeading instanceof HTMLElement) {
        selectionItemsHeading.textContent = this.#isSwitching
          ? this.dataset.switchItemsHeading ||
            "Select a different gift for your box"
          : this.dataset.selectionItemsHeading ||
            "Choose one gift to add to every box";
      }
    }

    if (footerText instanceof HTMLElement) {
      if (isPreview) {
        footerText.textContent =
          this.dataset.previewFooterText ||
          "Subscribe to unlock your free gift";
      } else if (this.#isSwitching) {
        footerText.textContent = this.#selectedGiftId
          ? this.dataset.switchFooterText ||
            "Your gift selection will be updated"
          : this.dataset.selectPromptText || "Select a gift to continue";
      } else {
        if (this.#selectedGiftId) {
          footerText.textContent = this.#skipAddonsRedirect
            ? "We'll add your gift to your next order"
            : this.dataset.selectionFooterText ||
              "We'll add your gift to your next order";
        } else {
          footerText.textContent =
            this.dataset.selectPromptText || "Select a gift to continue";
        }
      }
    }

    if (
      actionButton instanceof HTMLButtonElement &&
      actionButtonText instanceof HTMLElement
    ) {
      if (isPreview) {
        actionButtonText.textContent =
          this.dataset.previewButtonText || "Close Preview";
        actionButton.disabled = false;
        actionButton.classList.remove(
          "bg-grayscale-coal-100",
          "text-grayscale-coal-600",
        );
        actionButton.classList.add("bg-black-warm", "text-white");
      } else {
        if (this.#isSwitching) {
          actionButtonText.textContent =
            this.dataset.switchButtonText || "Switch Gift";
        } else if (this.#skipAddonsRedirect) {
          actionButtonText.textContent = "Claim Gift";
        } else {
          actionButtonText.textContent =
            this.dataset.claimButtonText || "Continue to add-ons";
        }

        actionButton.disabled = !this.#selectedGiftId || this.#loading;

        if (!this.#selectedGiftId) {
          actionButton.classList.add(
            "bg-grayscale-coal-100",
            "text-grayscale-coal-600",
          );
          actionButton.classList.remove("bg-black-warm", "text-white");
        } else {
          actionButton.classList.remove(
            "bg-grayscale-coal-100",
            "text-grayscale-coal-600",
          );
          actionButton.classList.add("bg-black-warm", "text-white");
        }
      }
    }

    this.#updateBackButtonVisibility();
    this.#updateGiftItemsUI();
    this.#updatePickUI(this.#mode === "preview");
  }

  // ==========================================================================
  // Pick-N GWP selection (ACQ-244)
  //
  // Self-contained region kept clear of the ACQ-210 cart-state plumbing
  // (#primeCart / #getCart / cart-state imports) so both rebases stay trivial.
  // Active only when getGwpTarget() >= 2; single-gift mode is untouched.
  // ==========================================================================

  /**
   * Toggle and populate the pick-2 UI. The single-select list is hidden in
   * pick mode and replaced by the pick grid; preview reuses the read-only rows
   * with pick copy.
   * @param {boolean} isPreview
   */
  #updatePickUI(isPreview) {
    const pickMode = this.#isPickMode();
    const { selectionItems, pickItems, pickCounter, footerText } = this.refs;

    if (pickMode && selectionItems instanceof HTMLElement) {
      selectionItems.classList.add("hidden");
    }
    if (pickItems instanceof HTMLElement) {
      // Drive the 2-col grid from inline styles, not a class: the theme base
      // defines `.grid{display:flex}` (shadowing Tailwind's `grid`), and a
      // {% stylesheet %} rule only compiles into compiled_assets on a full
      // theme push. Inline display wins over both and ships with this asset.
      const showPick = pickMode && !isPreview;
      pickItems.classList.toggle("hidden", !showPick);
      if (showPick) {
        pickItems.style.display = "grid";
        pickItems.style.gridTemplateColumns = "repeat(2, minmax(0, 1fr))";
        // Equal rows so all four cards match height and the Add/stepper controls
        // bottom-align (cards use h-full). No max-height on the grid: the outer
        // drawer body scrolls, so rows aren't compressed into clipping the card.
        pickItems.style.gridAutoRows = "minmax(0, 1fr)";
        pickItems.style.gap = "8px";
      } else {
        pickItems.style.display = "none";
      }
    }
    if (pickCounter instanceof HTMLElement) {
      pickCounter.classList.toggle("hidden", !pickMode || isPreview);
    }

    if (!pickMode) return;

    if (isPreview) {
      if (footerText instanceof HTMLElement) {
        footerText.textContent =
          this.dataset.pickPreviewFooterText ||
          "Subscribe to unlock 2 free gifts";
      }
      return;
    }

    this.#renderPickItems();
  }

  /** Sync the pick grid, counter, footer prompt, and action button to state. */
  #renderPickItems() {
    const target = this.#getTarget();
    const total = sumSelectedQuantity(this.#giftSelections);
    const atTarget = total >= target;

    this.querySelectorAll("[data-pick-item]").forEach((item) => {
      const variantId = item.getAttribute("data-variant-id") || "";
      const qty = quantityFor(this.#giftSelections, variantId);
      const selected = qty > 0;

      // No aria-pressed here: the card wrapper is not a button (the child
      // Add/stepper controls are the interactive elements); data-selected
      // carries the visual state.
      item.setAttribute("data-selected", String(selected));

      // Drive the selected border + shadow inline (not via the {% stylesheet %}
      // block): inline styles ship with this asset, whereas {% stylesheet %}
      // rules only recompile into compiled_assets on a full theme push. The
      // border lives on the [data-pick-border] overlay child, never the card
      // box, so the image stays flush to the card edge and selecting never
      // shifts layout (Figma 11026:1005 / 11026:1024).
      if (item instanceof HTMLElement) {
        const borderOverlay = item.querySelector("[data-pick-border]");
        if (borderOverlay instanceof HTMLElement) {
          borderOverlay.style.borderColor = selected ? "#000000" : "transparent";
        }
        item.style.boxShadow = selected
          ? "0 2px 8px rgba(0, 0, 0, 0.15)"
          : "0 2px 4px rgba(0, 0, 0, 0.15)";
      }

      const addButton = item.querySelector("[data-pick-add]");
      const stepper = item.querySelector("[data-pick-stepper]");
      const quantityEl = item.querySelector("[data-pick-qty]");
      const incButton = item.querySelector("[data-pick-inc]");

      if (addButton instanceof HTMLElement) {
        addButton.classList.toggle("hidden", selected);
        // The Add button keeps its DS v3 secondary outline (blue) in every
        // non-disabled state; the old yolk-outline mid state is retired.
      }
      if (addButton instanceof HTMLButtonElement)
        addButton.disabled = atTarget && !selected;
      if (stepper instanceof HTMLElement)
        stepper.classList.toggle("hidden", !selected);
      if (quantityEl instanceof HTMLElement)
        quantityEl.textContent = String(qty);
      if (incButton instanceof HTMLButtonElement) incButton.disabled = atTarget;
    });

    const {
      pickCounterText,
      pickCounterDots,
      footerText,
      actionButton,
      actionButtonText,
    } = this.refs;

    if (pickCounterText instanceof HTMLElement) {
      // Editor-configurable template, same pattern as the other pick-2 strings
      // (data-pick-prompt-text etc.); JS only substitutes the live counts.
      const counterTemplate =
        this.dataset.pickCounterText || "{selected} of {target} selected";
      pickCounterText.textContent = counterTemplate
        .replace("{selected}", String(total))
        .replace("{target}", String(target));
    }
    if (pickCounterDots instanceof HTMLElement) {
      pickCounterDots.replaceChildren();
      for (let i = 1; i <= target; i += 1) {
        // 12px dots, warm black (Figma 11026:1072). Banged classes: Tailwind
        // does not scan assets/*.js, so these must be classes the liquid
        // sources already compile (sections/custom-box-gift.liquid counter).
        const dot = document.createElement("span");
        dot.className = `!w-3 !h-3 !rounded-full !border !border-[#1A1614] !transition-colors ${
          i <= total ? "!bg-[#1A1614]" : "!bg-transparent"
        }`;
        pickCounterDots.appendChild(dot);
      }
    }

    const complete = isPickComplete(this.#giftSelections, target);

    if (footerText instanceof HTMLElement) {
      if (complete) {
        // Switching edits gifts already in the cart, so use the switch copy
        // ("...will be updated") rather than the claim copy that talks about
        // continuing to add-ons (parity with the single-gift switch path).
        footerText.textContent = this.#isSwitching
          ? this.dataset.switchFooterText ||
            "Your gift selection will be updated"
          : this.dataset.selectionFooterText ||
            "We'll add your gifts to your next order";
      } else {
        footerText.textContent =
          this.dataset.pickPromptText || `Select ${target} gifts to continue`;
      }
    }

    if (
      actionButton instanceof HTMLButtonElement &&
      actionButtonText instanceof HTMLElement
    ) {
      actionButtonText.textContent = this.#isSwitching
        ? this.dataset.switchButtonText || "Switch Gift"
        : this.dataset.claimButtonText || "Continue to add-ons";
      actionButton.disabled = !complete || this.#loading;
      if (complete) {
        actionButton.classList.remove(
          "bg-grayscale-coal-100",
          "text-grayscale-coal-600",
        );
        actionButton.classList.add("bg-black-warm", "text-white");
      } else {
        actionButton.classList.add(
          "bg-grayscale-coal-100",
          "text-grayscale-coal-600",
        );
        actionButton.classList.remove("bg-black-warm", "text-white");
      }
    }
  }

  /**
   * Add one unit of a gift (pick-2). Bound via `on:click="/pickAdd"`; also used
   * by the stepper "+". Appends a new entry (two unique) or increments an
   * existing one (two of the same), capped at the target.
   * @param {Event} event
   */
  pickAdd(event) {
    const target =
      event.target instanceof Element
        ? event.target.closest("[data-pick-item]")
        : null;
    if (!target) return;

    const variantId = target.getAttribute("data-variant-id");
    const productTitle = target.getAttribute("data-product-title") || "";
    if (!variantId) return;

    // Any user pick supersedes an in-flight pre-seed so a late /cart.js result
    // can't clobber a live choice, including one toggled back down to zero.
    this.#seedRequestId += 1;
    this.#giftSelections = addUnit(
      this.#giftSelections,
      { variantId, productTitle },
      this.#getTarget(),
    );
    this.#renderPickItems();
    void this.#trackGiftSelected(variantId, productTitle);
  }

  /**
   * Remove one unit of a gift (pick-2). Bound via `on:click="/pickRemove"`.
   * @param {Event} event
   */
  pickRemove(event) {
    const target =
      event.target instanceof Element
        ? event.target.closest("[data-pick-item]")
        : null;
    if (!target) return;

    const variantId = target.getAttribute("data-variant-id");
    if (!variantId) return;

    // Any user pick supersedes an in-flight pre-seed (see pickAdd).
    this.#seedRequestId += 1;
    this.#giftSelections = removeUnit(this.#giftSelections, variantId);
    this.#renderPickItems();
  }

  /**
   * Pick-N switch pre-seed: read the box's current gift lines from the cart and
   * load them into #giftSelections so the pick UI opens with them already
   * selected. Gifts are matched by the parent's _line_item_id (the same key the
   * commit path links on). Best-effort: on any failure the drawer just opens
   * empty, which is the prior behaviour.
   */
  async #seedPickSelectionsFromCart() {
    // Token + snapshot so a slow /cart.js fetch can't clobber live state: if the
    // user changes a pick, closes, or reopens for another box while we wait, we
    // discard this seed instead of overwriting their selection.
    const requestId = ++this.#seedRequestId;
    const parentKeyAtStart = this.#parentItemKey;
    try {
      const cart = await this.#getCart(true);

      if (
        requestId !== this.#seedRequestId || // a newer open superseded this one
        this.#mode !== "selection" || // drawer closed
        !this.#isSwitching || // no longer a switch
        this.#parentItemKey !== parentKeyAtStart || // different parent now
        sumSelectedQuantity(this.#giftSelections) !== 0 // user already picked
      ) {
        return;
      }

      const items = (cart && cart.items) || [];

      let parentUuid = this.#parentUuid;
      if (!parentUuid && this.#parentItemKey) {
        const parent = resolveParentLine(cart, {
          key: parentKeyAtStart,
          variantId: this.#parentItemId,
          sellingPlanId: this.#parentSellingPlanId,
        });
        parentUuid = parent?.properties?._line_item_id || null;
      }
      if (!parentUuid) return;

      const giftLines = items.filter(
        /** @param {any} item */
        (item) =>
          item.properties?._is_pyp_gift === "true" &&
          item.properties?._parent_item_id === parentUuid,
      );
      if (giftLines.length === 0) return;

      this.#giftSelections = groupGiftLinesToSelections(giftLines).map(
        /** @param {any} selection */
        (selection) => ({
          variantId: String(selection.variantId),
          quantity: selection.quantity,
          productTitle: selection.title || "",
        }),
      );
      this.#renderPickItems();
    } catch (error) {
      console.error(
        "[GiftSelection] Failed to pre-seed switch selection:",
        error,
      );
    }
  }

  /**
   * Persist the validated pick-N selections to the cart (ACQ-247 T8).
   *
   * Resolves (or mints) the parent box UUID with the same flow as the single-gift
   * add (#addParentProperties), builds the slotted line items with the shared
   * buildGiftLineItems helper, then on the ONE shared queue removes every existing
   * gift under the parent FIRST and adds the new set (remove-then-add, D6: a
   * partial failure leaves the box giftless and re-pickable, never double-gifted).
   * On failure the drawer stays open (retryable) and does not navigate.
   */
  async #commitGiftSelections() {
    if (this.#loading) return;
    this.#loading = true;
    this.#updateUIForMode();

    let failed = false;
    try {
      if (!this.#parentItemKey) {
        throw new Error("Cannot add gifts: parent item key is missing");
      }
      if (sumSelectedQuantity(this.#giftSelections) === 0) {
        this.closeDialog();
        return;
      }

      const sectionsToUpdate = collectCartSections();

      // Snapshot the commit inputs before enqueue: the op may wait behind other
      // cart operations, and the drawer could close/reset in that window. Bind
      // the op to these immutable locals so a late run can't target null state
      // or write a different later selection.
      const parentSnapshot = {
        key: this.#parentItemKey,
        uuid: this.#parentUuid,
        variantId: this.#parentItemId,
        sellingPlanId: this.#parentSellingPlanId,
      };
      const parentUuidFromState = this.#parentUuid;
      const giftSelections = this.#giftSelections.map((selection) => ({
        ...selection,
      }));

      // Everything that touches the cart (resolve/mint the parent UUID, remove
      // old gifts, add new) runs INSIDE the one queued op so it serializes
      // against the cart drawer / reconciler — no stamp write off-queue.
      await cartOperationQueue.enqueue(async () => {
        const fresh = await getCart({ force: true });
        // Resolve the parent's CURRENT line: the key we were handed may have
        // been re-keyed by a mutation that ran before this op.
        const parentItem = resolveParentLine(fresh, parentSnapshot);
        if (!parentItem) throw new Error("Parent item not found in cart");

        const props = parentItem.properties || {};
        let parentUuid = props._line_item_id || parentUuidFromState;
        const bundleId = props._rb_id || props._bundle_id || null;
        if (!parentUuid) {
          parentUuid = crypto.randomUUID();
          // pickN=true: do NOT stamp _has_double_gwp on a pick-N parent (D9).
          // Throw on a failed stamp: adding gift lines under a UUID that never
          // landed on the parent would orphan them (invisible to switch/remove
          // flows). The catch below keeps the drawer open and retryable.
          const stamped = await this.#addParentProperties(
            parentItem,
            parentUuid,
            true,
            parentSnapshot,
          );
          if (!stamped) {
            throw new Error("Failed to stamp parent UUID on parent item");
          }
        }

        // Remove ALL existing gifts under this parent FIRST (remove-then-add).
        const oldGifts = (fresh.items || []).filter(
          /** @param {any} item */
          (item) =>
            item.properties?._is_pyp_gift === "true" &&
            item.properties?._parent_item_id === parentUuid,
        );
        for (const gift of oldGifts) {
          await this.#updateCartItem(gift.key || gift.id?.toString(), 0);
        }

        const giftLines = buildGiftLineItems(giftSelections, {
          parentUuid,
          bundleId,
        });
        if (giftLines.length === 0) return;

        // Add the new slotted gift line(s) in one /cart/add.js call.
        /** @type {Record<string, any>} */
        const body = { items: giftLines };
        if (sectionsToUpdate.length > 0) {
          body.sections = sectionsToUpdate.join(",");
        }
        const response = await fetch(Theme.routes.cart_add_url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            Accept: "application/json",
          },
          body: JSON.stringify(body),
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          throw new Error(data.message || "Failed to add gift items");
        }
        if (data.cart) this.#primeCart(data.cart);

        this.dispatchEvent(
          new CartUpdateEvent({}, "gift-selection-drawer", {
            source: "gift-selection-drawer",
            sections: data.sections,
            cart: data.cart,
            itemCount: data.item_count,
          }),
        );
      });

      this.closeDialog();

      if (
        !this.#skipAddonsRedirect &&
        !window.location.pathname.includes("/pre-checkout-add-ons")
      ) {
        const addonsUrl =
          this.dataset.addonsUrl || "/collections/pre-checkout-add-ons";
        window.location.href = addonsUrl;
      }
    } catch (error) {
      // Leave the drawer open so the user can retry; do not navigate.
      console.error(
        "[GiftSelection] Failed to commit pick-2 selections:",
        error,
      );
      failed = true;
    } finally {
      this.#loading = false;
      this.#updateUIForMode();
      // After the refresh, or #renderPickItems would overwrite it.
      if (failed) this.#showFooterError();
    }
  }

  #updateBackButtonVisibility() {
    const { backButton } = this.refs;
    if (backButton instanceof HTMLButtonElement) {
      backButton.classList.toggle("hidden", !this.#openedFromLearnMore);
    }
  }

  handleBackButton() {
    this.#openedFromLearnMore = false;
    delete this.dataset.openedFromLearnMore;
    this.closeDialog();
  }

  handleCloseButton() {
    if (this.#openedFromLearnMore) {
      document.dispatchEvent(
        new CustomEvent("gift-drawer:close-requested", {
          detail: { fromLearnMore: true },
        }),
      );
    }
    this.closeDialog();
  }

  #updateGiftItemsUI() {
    const giftItems = this.querySelectorAll("[data-gift-item]");

    giftItems.forEach((item) => {
      const variantId = item.getAttribute("data-variant-id");
      const isSelected = variantId === this.#selectedGiftId;
      const isPreview = this.#mode === "preview";

      item.setAttribute("data-selected", String(isSelected));
      item.setAttribute("aria-pressed", String(isSelected));

      if (isPreview) {
        item.classList.remove(
          "p-[8px]",
          "cursor-pointer",
          "bg-white",
          "rounded-[4px]",
          "border-[3px]",
          "border-black-warm",
          "border-[1px]",
          "bg-gradient-to-r",
          "from-[rgba(189,176,147,0.3)]",
          "to-[rgba(189,176,147,0.3)]",
        );
        item.classList.add(
          "px-0",
          "py-[8px]",
          "h-[101px]",
          "border-t",
          "border-grayscale-coal-200",
        );
      } else {
        item.classList.remove("px-0", "py-[8px]", "h-[101px]", "border-t");
        item.classList.add(
          "p-[8px]",
          "h-[106px]",
          "cursor-pointer",
          "bg-white",
          "rounded-[4px]",
        );

        if (isSelected) {
          item.classList.remove("border-[1px]", "border-grayscale-coal-200");
          item.classList.add(
            "border-[3px]",
            "border-black-warm",
            "bg-gradient-to-r",
            "from-[rgba(189,176,147,0.3)]",
            "to-[rgba(189,176,147,0.3)]",
          );
        } else {
          item.classList.remove(
            "border-[3px]",
            "border-black-warm",
            "bg-gradient-to-r",
            "from-[rgba(189,176,147,0.3)]",
            "to-[rgba(189,176,147,0.3)]",
          );
          item.classList.add("border-[1px]", "border-grayscale-coal-200");
        }
      }
    });
  }

  /**
   * @param {MouseEvent} event
   * @param {Object} _data
   */
  selectGift(event, _data) {
    if (this.#mode === "preview") return;

    const target =
      event.target instanceof Element
        ? event.target.closest("[data-gift-item]")
        : null;
    if (!target) return;

    const variantId = target.getAttribute("data-variant-id");
    const productTitle = target.getAttribute("data-product-title");

    if (variantId) {
      this.#selectedGiftId = variantId;
      this.#updateUIForMode();

      void this.#trackGiftSelected(variantId, productTitle || "");

      this.dispatchEvent(
        new GiftSelectedEvent({
          variantId,
          productTitle: productTitle || "",
          parentItemId: this.#parentItemId || "",
        }),
      );
    }
  }

  /**
   * @param {KeyboardEvent} event
   * @param {Object} _data
   */
  handleGiftKeydown(event, _data) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.selectGift(event, _data);
    }
  }

  async handleAction() {
    if (this.#mode === "preview") {
      this.closeDialog();
      return;
    }

    // ACQ-244: pick-2 routes to the deferred cart-write seam once the
    // sum(quantity) === target gate passes. Single-gift falls through below.
    if (this.#isPickMode()) {
      if (!isPickComplete(this.#giftSelections, this.#getTarget())) return;
      await this.#commitGiftSelections();
      return;
    }

    if (!this.#selectedGiftId || !this.#parentItemKey) {
      console.error("Cannot add gift: missing gift ID or parent item key");
      return;
    }

    if (
      this.#isSwitching &&
      this.#selectedGiftId === this.#originalGiftVariantId
    ) {
      this.closeDialog();
      return;
    }

    await this.#addGiftToCart();
  }

  #resetState() {
    this.#selectedGiftId = null;
    this.#giftSelections = [];
    this.#originalGiftVariantId = null;
    this.#parentItemId = null;
    this.#parentItemKey = null;
    this.#parentUuid = null; // ← ADD THIS
    this.#parentSellingPlanId = null;
    this.#existingGiftLineId = null;
    this.#isSwitching = false;
    this.#skipAddonsRedirect = false;
    this.#parentQuantity = 1;
    this.#hasTrackedSelectionView = false;
  }

  #handleDialogClose = () => {
    delete this.dataset.openedFromLearnMore;
    this.#openedFromLearnMore = false;

    this.#resetState();
  };

  async #getAnalyticsCartContext() {
    if (!window.grAnalytics?.buildCartContextFromCart) return undefined;

    try {
      const cart = await this.#getCart();
      return window.grAnalytics.buildCartContextFromCart(cart);
    } catch (error) {
      console.error("[GiftSelection] Failed to get cart for analytics:", error);
      return undefined;
    }
  }

  async #trackGiftSelectionViewed() {
    const dialog = this.refs.dialog;
    if (dialog instanceof HTMLDialogElement && !dialog.open) return;

    if (this.#hasTrackedSelectionView) return;
    this.#hasTrackedSelectionView = true;

    const cartContext = await this.#getAnalyticsCartContext();
    window.grAnalytics?.trackGiftSelectionViewed({}, cartContext);
  }

  async #trackGiftSelected(variantId, productTitle) {
    const cartContext = await this.#getAnalyticsCartContext();
    window.grAnalytics?.trackGiftSelected(
      {
        gift_product_name: productTitle || undefined,
        gift_variant_id: variantId || undefined,
      },
      cartContext,
    );
  }

  /**
   * Pick target for this surface, clamped to the 1|2 contract.
   *
   * Read from the `data-gwp-pick-count` attribute (rendered by Liquid from
   * `settings.gwp_pick_count`) rather than the shared getGwpTarget(): this file
   * runs as a raw `<script type="module">`, which cannot resolve the
   * classic-script global lexical `Theme`, so getGwpTarget() returns 1 here. The
   * data attribute is the module-safe source of truth. Falls back to
   * getGwpTarget() when the attribute is absent (e.g. an older render).
   * @returns {number} 1 or 2
   */
  #getTarget() {
    const raw = Number(this.dataset.gwpPickCount);
    if (!Number.isFinite(raw)) return getGwpTarget();
    return Math.min(2, Math.max(1, Math.round(raw)));
  }

  #getGwpMultiplier() {
    // The count>=2 multi-select path routes through #commitGiftSelections (the
    // ACQ-247 seam), so this multiplier only applies to single-gift mode. There,
    // preserve the legacy double_gwp_enabled doubling (one gift at qty 2) so the
    // cart quantity matches the _has_double_gwp property written below and the
    // custom-box section's legacy_double behavior.
    if (this.#isPickMode()) return 1;
    return Theme?.features?.double_gwp_enabled ? 2 : 1;
  }

  /** True when the pick-2 promo is active (target >= 2). */
  #isPickMode() {
    return this.#getTarget() >= 2;
  }

  async #addGiftToCart() {
    if (this.#loading) return;
    this.#loading = true;
    this.#updateUIForMode();

    let failed = false;
    try {
      if (!this.#selectedGiftId) {
        console.error("No gift selected");
        return;
      }
      if (!this.#parentUuid && !this.#parentItemKey) {
        console.error("Cannot add gift: parent UUID and key are both missing");
        return;
      }

      // Snapshot the inputs before enqueue (same reason as the pick-N commit):
      // the op may wait behind other cart operations and the drawer could
      // close/reset in that window.
      const selectedGiftId = this.#selectedGiftId;
      const existingGiftLineId = this.#existingGiftLineId;
      const quantity = this.#parentQuantity * this.#getGwpMultiplier();
      const parentSnapshot = {
        key: this.#parentItemKey,
        uuid: this.#parentUuid,
        variantId: this.#parentItemId,
        sellingPlanId: this.#parentSellingPlanId,
      };
      const sectionsToUpdate = collectCartSections();

      // Resolve/stamp the parent, remove the old gift, add the new one INSIDE
      // one queued op so it serializes against the free-gift reconciler and
      // the cart drawer (parity with the pick-N commit).
      const data = await cartOperationQueue.enqueue(async () => {
        const cart = await this.#getCart(true);

        let parentUuid = parentSnapshot.uuid;
        if (!parentUuid) {
          // Resolve the parent's CURRENT line; the key we were handed may
          // have been re-keyed by an earlier mutation.
          const parentItem = resolveParentLine(cart, parentSnapshot);
          if (!parentItem) {
            throw new Error(
              `Parent item not found in cart (key ${parentSnapshot.key})`,
            );
          }

          const props = /** @type {ParentItemProperties} */ (
            parentItem.properties || {}
          );
          parentUuid = props._line_item_id || null;

          if (!parentUuid) {
            parentUuid = crypto.randomUUID();
            const stamped = await this.#addParentProperties(
              parentItem,
              parentUuid,
              false,
              parentSnapshot,
            );
            if (!stamped) {
              throw new Error("Failed to stamp parent UUID on parent item");
            }
          }
        }

        // Switch: remove the old gift AFTER the parent is resolved, by its
        // CURRENT key from the fresh cart (gift lines get re-keyed too), so a
        // failure later in this op never leaves the box giftless with a dead
        // key the next attempt would post again. Match by the parent link
        // first; the cached key is only a fallback for an unlinked legacy gift.
        // A gift linked to a DIFFERENT box is never removed here, even if its
        // key was cached (#processCartForGift can pick up a sibling box's gift).
        if (existingGiftLineId) {
          const oldGifts = (cart?.items || []).filter(
            /** @param {any} item */
            (item) => {
              if (item.properties?._is_pyp_gift !== "true") return false;
              const linkedTo = item.properties?._parent_item_id;
              if (linkedTo) return linkedTo === parentUuid;
              return item.key === existingGiftLineId;
            },
          );
          for (const gift of oldGifts) {
            await this.#updateCartItem(gift.key || gift.id?.toString(), 0);
          }
        }

        const properties = {
          _is_pyp_gift: "true",
          _parent_item_id: parentUuid,
        };

        const formData = new FormData();
        formData.append("id", selectedGiftId);
        formData.append("quantity", String(quantity));

        for (const [key, value] of Object.entries(properties)) {
          formData.append(`properties[${key}]`, value);
        }

        if (sectionsToUpdate.length > 0) {
          formData.append("sections", sectionsToUpdate.join(","));
        }

        const response = await fetch(Theme.routes.cart_add_url, {
          ...fetchConfig("javascript", { body: formData }),
          headers: {
            "X-Requested-With": "XMLHttpRequest",
            Accept: "application/json",
          },
        });

        const body = await response.json();
        if (!response.ok || body.status) {
          throw new Error(body.message || "Failed to add gift to cart");
        }
        return body;
      });

      if (data.cart) {
        this.#primeCart(data.cart);
      }

      this.dispatchEvent(
        new CartUpdateEvent({}, "gift-selection-drawer", {
          source: "gift-selection-drawer",
          sections: data.sections,
          cart: data.cart,
          itemCount: data.item_count,
        }),
      );

      this.closeDialog();

      if (
        !this.#isSwitching &&
        !this.#skipAddonsRedirect &&
        !window.location.pathname.includes("/pre-checkout-add-ons")
      ) {
        const addonsUrl =
          this.dataset.addonsUrl || "/collections/pre-checkout-add-ons";
        window.location.href = addonsUrl;
      }
    } catch (error) {
      // Leave the drawer open so the user can retry; do not navigate.
      console.error("Error adding gift to cart:", error);
      failed = true;
    } finally {
      this.#loading = false;
      this.#updateUIForMode();
      // After the refresh, or #updateUIForMode would overwrite it.
      if (failed) this.#showFooterError();
    }
  }

  /**
   * A cart write failed: tell the shopper in the footer. The drawer stays
   * open with the selection intact so they can retry.
   */
  #showFooterError() {
    const { footerText } = this.refs;
    if (footerText instanceof HTMLElement) {
      footerText.textContent =
        this.dataset.errorText ||
        "We couldn't update your cart. Please try again.";
    }
  }

  /**
   * @param {string} lineId
   * @param {number} quantity
   */
  async #updateCartItem(lineId, quantity) {
    const body = JSON.stringify({ id: lineId, quantity });
    const response = await fetch(
      Theme.routes.cart_change_url,
      fetchConfig("json", { body }),
    );
    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(`Cart change failed (${response.status}): ${text}`);
    }

    // Some Shopify endpoints return JSON even for change.js; parse defensively to detect errors.
    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      const data = await response.json().catch(() => null);
      if (data?.errors) {
        throw new Error(`Cart change error: ${JSON.stringify(data.errors)}`);
      }
    }
  }

  /** @returns {'preview' | 'selection'} */
  get mode() {
    return this.#mode;
  }

  /** @returns {string | null} */
  get selectedGiftId() {
    return this.#selectedGiftId;
  }

  /**
   * Adds _line_item_id and _parent_product properties to a parent item.
   *
   * Called with the parent line as just resolved from a fresh cart. If Shopify
   * rejects that key as stale anyway (a write landed between read and write,
   * or read-replica lag), the parent is resolved again from `parent` and the
   * write is retried once.
   *
   * @param {any} parentItem - The parent line as resolved from a fresh cart
   * @param {string} uuid - The UUID to use as the line item ID
   * @param {boolean} [pickN=false] - When true (pick-N), do NOT stamp
   *   `_has_double_gwp` (pick-N overrides double_gwp; the gift lines clamp to 1).
   * @param {{ uuid?: string | null, key?: string | null, variantId?: string | null, sellingPlanId?: string | null }} [parent]
   *   Resolution criteria for the single retry.
   * @returns {Promise<boolean>} true when the parent carries the properties
   *   (written now or already present); false on any failure. Never throws:
   *   both commit paths throw on false so gifts are never added under a UUID
   *   that was never persisted on the parent line (orphaned gifts).
   */
  async #addParentProperties(parentItem, uuid, pickN = false, parent = {}) {
    try {
      let target = parentItem;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        if (!target) {
          console.error("Parent item not found in cart");
          return false;
        }

        // Check if properties already exist - skip if they do
        const props = /** @type {ParentItemProperties} */ (
          target.properties || {}
        );
        if (props._line_item_id && props._parent_product === "yes") {
          console.log("[Parent Properties] Already exist, skipping update");
          return true;
        }

        /** @type {Record<string, string>} */
        const nextProps = {
          ...(target.properties || {}),
          _line_item_id: uuid,
          _parent_product: "yes",
        };
        // Legacy single-gift parents carry _has_double_gwp; pick-N parents do not
        // (D9). Spreading the existing properties could carry a stale flag, so
        // strip it explicitly on the pick-N path.
        if (!pickN) {
          nextProps._has_double_gwp = Theme?.features?.double_gwp_enabled
            ? "true"
            : "false";
        } else {
          delete nextProps._has_double_gwp;
        }

        const body = JSON.stringify({
          id: target.key,
          quantity: target.quantity,
          properties: nextProps,
        });

        const response = await fetch(
          Theme.routes.cart_change_url,
          fetchConfig("json", { body }),
        );
        const result = await response.json().catch(() => null);

        if (response.ok && isCartResponse(result) && !result.errors) {
          this.#primeCart(result);
          // Stamping properties re-keys the line; keep our reference current.
          const stamped = findParentLine(result, { lineItemId: uuid });
          if (stamped?.key) this.#parentItemKey = stamped.key;
          return true;
        }

        if (attempt === 0 && isStaleLineError(response.status, result)) {
          const cart = await this.#getCart(true);
          target = resolveParentLine(cart, {
            ...parent,
            key: target.key,
          });
          continue;
        }

        console.error("Failed to add parent properties", {
          status: response.status,
          errors: result?.errors || result?.message || null,
        });
        return false;
      }
      return false;
    } catch (error) {
      console.error("Error adding parent properties:", error);
      return false;
    }
  }
}

if (!customElements.get("gift-selection-drawer")) {
  customElements.define("gift-selection-drawer", GiftSelectionDrawerComponent);
}
